import {
  Box,
  Divider,
  Flex,
  PrimaryButton,
  RoundedButton,
  SecondaryButton,
  SystemProps,
  LegacyText,
  Text,
} from "flicket-ui";
import { useContext } from "react";
import styled from "styled-components";
import { Icon } from "~components/common/Icon";
import {
  AlcoholFreeZoneTypes,
  CoverTypes,
  SeatSelectTypes,
} from "~components/reservation/constants";
import { formatSeatLocation } from "~lib";
import { Placeholder } from "~components/reservation/options/options.Placeholder";
import { useSeatedReservationStore } from "~features/seated-reservation/store";
import { EventQuery, IntegrationType } from "~graphql/sdk";
import { Pricing } from "../components/sidebar/PricingList";
import { List } from "../components/sidebar/ZoneSectionList";
import { Release } from "./../types";
import { UserContext } from "~context";
import * as actions from "../store/actions/index";
import useActiveReferralCampaign from "~features/referrals/useActiveReferralCampaign";
import { isBNPLIntegrationType } from "~features/checkout/common/checkout.types";
import { useSeekaConverge } from "@seeka-labs/converge-react";
import pluralize from "pluralize";
import { CreateOrderOptions } from "~hooks/reservation/useCreateOrder";

type SidebarProps = {
  event: EventQuery["event"];
  release: Release;
  changeTickets: any[];
  handleOrderCreation: (options?: CreateOrderOptions) => Promise<boolean>;
  priceComp: number | undefined;
  allowPublicComp?: boolean;
};

const StyledRoundedButton = styled(RoundedButton)<SystemProps>`
  height: 32px;
  width: 32px;
  padding: 0;

  display: flex !important;
  align-items: center;
  justify-content: center;

  border: 1px solid ${(p) => p.theme.colors.N300};

  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.03),
    0px 1.6711px 8px rgba(0, 0, 0, 0.04), 0px 0.893452px 5px rgba(0, 0, 0, 0.03),
    0px 0.500862px 3.63125px rgba(0, 0, 0, 0.02),
    0px 0.266004px 1.92853px rgba(0, 0, 0, 0.015),
    0px 0.11069px 0.802504px rgba(0, 0, 0, 0.01);
`;

const SidebarLayout = ({
  event,
  release,
  changeTickets,
  handleOrderCreation,
  priceComp,
  allowPublicComp = false,
}: SidebarProps) => {
  const converge = useSeekaConverge();
  const seats = useSeatedReservationStore((state) => state.seats);
  const sidebarIndex = useSeatedReservationStore((state) => state.sidebarIndex);
  const options = useSeatedReservationStore((state) => state.options);
  const chartReady = useSeatedReservationStore((state) => state.chartReady);
  const currentZone = useSeatedReservationStore((state) => state.currentZone);
  const currentSection = useSeatedReservationStore(
    (state) => state.currentSection
  );
  const selectableZones = useSeatedReservationStore(
    (state) => state.selectableZones
  );
  const busyState = useSeatedReservationStore((state) => state.busyState);
  const validSeatSelection = useSeatedReservationStore(
    (state) => state.validSeatSelection
  );

  const { isAdmin, isPOS } = useContext(UserContext);

  const addons = changeTickets?.length
    ? []
    : release?.releaseEventAddons.map(({ eventAddon }) => eventAddon);

  const { activeReferralCampaign } = useActiveReferralCampaign(event.id);

  const handleBackButton = async () => {
    await actions.onSidebarBack();
  };

  const navigateCheckout = async (options: CreateOrderOptions = {}) => {
    if (!options.isExpressCheckout) {
      actions.setBusyState({ state: "creating-order" });
    }

    const success = await handleOrderCreation(options);

    if (!success) {
      actions.setBusyState({ state: "idle" });
    }
  };

  const bnplGateways = event.gateways?.filter(
    (gateway) => !!gateway && isBNPLIntegrationType(gateway.type)
  );

  const stripeAfterpayGateway = event.gateways?.find(
    (gateway) =>
      gateway.type === IntegrationType.PaymentStripe && gateway.bnplEnabled
  );

  const breadcrumbs = (() => {
    // @todo: later on let them choose namings aisle vs section

    let crumbs = "";

    // if (currentSection?.name) {
    //   crumbs = currentSection?.name + ", ";
    // }

    if (currentZone?.name) {
      crumbs += currentZone?.name + ", ";
    }

    crumbs += event.venue.name;

    if (!currentZone) {
      crumbs += " zones";
    }
    return crumbs;
  })();

  if (!chartReady && !selectableZones?.length) {
    return <Placeholder />;
  }

  return (
    <Box
      p={{ _: 2, md: 4 }}
      pb={2}
      height={{ _: "calc(100% - 342px)", sm: "calc(100% - 530px)", md: "100%" }}
      maxHeight={{
        _: "calc(100% - 342px)",
        sm: "calc(100% - 530px)",
        md: "100%",
      }}
      overflowY="auto"
      backgroundColor="white"
    >
      {event.isR18 && (
        <Flex
          alignItems="center"
          borderRadius="sm"
          backgroundColor="red.100"
          px="6/4"
          py={2}
          mb={2}
        >
          <Icon icon="r18" fontSize={6} mr={1} />
          <LegacyText
            fontWeight="medium"
            fontSize={2}
            lineHeight="medium"
            color="N700"
          >
            This is an R18 event
          </LegacyText>
        </Flex>
      )}
      <Flex mb={{ _: "6/4", md: 2 }} alignItems="center" minH={32}>
        {sidebarIndex > 1 && (
          <StyledRoundedButton
            bg="white"
            onClick={() => void handleBackButton()}
            mr="6/4"
            d={{ _: "none", md: "block" }}
            py={0}
            flexShrink={0}
          >
            <Icon fontSize={6} icon="chevron-left" />
          </StyledRoundedButton>
        )}
        <LegacyText
          fontWeight="heavy"
          fontSize={4}
          color="N800"
          lineHeight={1.2 as any}
        >
          {breadcrumbs}
        </LegacyText>
      </Flex>
      <Divider />

      {!!changeTickets?.length && (
        <Box bg="N200" mt={2} py={1} px="6/4" borderRadius="sm">
          <Text variant="regular">
            Your original {pluralize("seat", changeTickets?.length)}
          </Text>
          {changeTickets?.map((changeTicket) => (
            <Text key={changeTicket.id} variant="small">
              {formatSeatLocation(
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                changeTicket.seatZone,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                changeTicket.seatSection,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                changeTicket.seatLabel
              )}
            </Text>
          ))}
        </Box>
      )}
      <Box py={1} mt={{ _: "1/2", md: 2 }}>
        {selectableZones?.length > 0 && (
          <>
            {sidebarIndex === 1 && (
              <List
                key="zone"
                index="zone"
                currentSort={options.selectedSort}
                pricing={currentZone?.priceRange}
                zones={selectableZones}
                selected={currentZone}
              />
            )}

            {sidebarIndex === 2 && (
              <List
                key="section"
                index="section"
                currentSort={options.selectedSort}
                pricing={currentZone?.priceRange}
                zones={currentZone}
                selected={currentSection}
              />
            )}

            {(sidebarIndex === 3 || addons?.length > 0) && (
              <Pricing
                key="pricing"
                type={"event"}
                bookingFee={event.bookingFee}
                bookingFeeType={event.bookingFeeType}
                changingSeatsFee={event.changingSeatsFee}
                addons={addons}
                changeTickets={changeTickets}
                navigateCheckout={navigateCheckout}
                isNavigating={busyState === "creating-order"}
                onBack={() => void handleBackButton()}
                bnplGateways={bnplGateways}
                stripeAfterpayGateway={stripeAfterpayGateway}
                priceComp={priceComp}
                allowPublicComp={allowPublicComp}
                activeReferralCampaign={activeReferralCampaign}
                paymentPlanSettings={event.paymentPlanSettings}
                fees={event.fees}
              />
            )}
          </>
        )}

        {!selectableZones?.length && (
          <LegacyText>No options available.</LegacyText>
        )}
        {options.filterActive && (
          <>
            <SecondaryButton
              w="100%"
              mt={3}
              onClick={() => {
                void actions.updateOptions(
                  {
                    options: {
                      filterActive: false,
                      seatSelectType: SeatSelectTypes.QUICK_SELECT,
                      coverType: CoverTypes.ALL,
                      zoneType: AlcoholFreeZoneTypes.NO_PREFERENCE,
                    },
                  },
                  converge
                );
              }}
            >
              Reset filters
            </SecondaryButton>
          </>
        )}

        {sidebarIndex !== 3 && !!seats?.length && (
          <>
            <PrimaryButton
              isLoading={busyState === "creating-order"}
              mt={2}
              w="100%"
              disabled={validSeatSelection === false && !isAdmin && !isPOS}
              onClick={() => void navigateCheckout()}
            >
              {validSeatSelection === true || isAdmin || isPOS
                ? `Buy ${seats?.length} selected ticket${
                    seats?.length > 1 ? "s" : ""
                  }`
                : "Can not leave a single seat"}
            </PrimaryButton>
          </>
        )}
      </Box>
    </Box>
  );
};

export default SidebarLayout;
